import cn from 'classnames';
import {useSelector} from 'react-redux';
import {FormattedMessage, useIntl} from 'react-intl';
import {Close, AddCircleRounded} from '@material-ui/icons';
import React, {useCallback, useMemo, useState} from 'react';
import {Box, Button, Dialog, IconButton, Toolbar} from '@material-ui/core';

import {Navigation} from '../Navigation';
import {LabelReview} from './LabelReview';
import {RootState, useAppDispatch} from '../../../store';
import {DropArea} from '../../../base-components/StudioDropArea';
import Waiting from '../../../base-components/StudioWaiting/Waiting';
import {StudioTextField} from '../../../base-components/StudioTextField';
import FormButton from '../../../base-components/StudioButton/FormButton';
import {
  DataPrepareStep,
  mergeCategories,
  dataPrepareActions,
} from '../../../store/dataPrepare';
import {
  CategoryType,
  AnnotationDataType,
} from '../../../types/dataset/DatasetGetAnnotationsResponse';

import './LabelView.scss';

type LabelViewProps = {
  projectId: string;
  inProgress: boolean;
  dataPreparationContainerId?: string;
  onBack: () => void;
  onForward: () => void;
};

const {setStep, setIsAddMoreDialogOpen} = dataPrepareActions;

export const LabelView = ({
  inProgress,
  dataPreparationContainerId,
  onBack,
  onForward,
}: LabelViewProps) => {
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const [isUploadingLabels, setIsUploadingLabels] = useState(false);
  const step = useSelector((state: RootState) => state.dataPrepare.step);
  const annotationData = useSelector(
    (state: RootState) => state.dataPrepare.annotationData
  );
  const isAddMoreDialogOpen = useSelector(
    (state: RootState) => state.dataPrepare.isAddMoreDialogOpen
  );

  const currentCategories = useMemo(() => annotationData?.categories || [], [
    annotationData,
  ]);
  const currentCategoriesMap = useMemo(
    () =>
      currentCategories.reduce<{
        [key: string]: CategoryType;
      }>((acc, curr) => ({...acc, [curr.name]: curr}), {}),
    [currentCategories]
  );

  const handleSave = useCallback(
    async (updatedAnnotations: AnnotationDataType) => {
      if (dataPreparationContainerId) {
        setIsUploadingLabels(true);

        await dispatch(
          mergeCategories({
            updatedAnnotations,
            resourceId: dataPreparationContainerId,
          })
        );

        setIsUploadingLabels(false);
      }
    },
    [dispatch, dataPreparationContainerId]
  );

  const handleAdditionalLabels = useCallback(
    (labels: string[]) => {
      if (dataPreparationContainerId && annotationData) {
        const maxCategoryId = currentCategories.reduce(
          (acc, curr) => Math.max(acc, curr.id),
          0
        );
        const dedupedLabels = labels.filter(label => !currentCategoriesMap[label]);
        const newCategories = dedupedLabels.map<CategoryType>((label, index) => ({
          id: maxCategoryId + index + 1,
          name: label,
        }));

        dispatch(setStep(DataPrepareStep.LABEL_REVIEW));
        handleSave({
          ...annotationData,
          categories: [...currentCategories, ...newCategories],
        });
      }
    },
    [
      annotationData,
      currentCategories,
      currentCategoriesMap,
      dataPreparationContainerId,
      dispatch,
      handleSave,
    ]
  );

  const handleUpdatedLabels = useCallback(
    (labels: CategoryType[]) => {
      if (dataPreparationContainerId && annotationData) {
        const newAnnotations = annotationData.annotations.filter(({category_id}) =>
          labels.some(label => label.id === category_id)
        );

        handleSave({
          ...annotationData,
          categories: labels,
          annotations: newAnnotations,
        });
      }
    },
    [annotationData, dataPreparationContainerId, handleSave]
  );

  const isValid = useMemo(() => Boolean(annotationData?.categories?.length), [
    annotationData,
  ]);
  const disableForward = useMemo(() => !isValid || inProgress, [isValid, inProgress]);

  return (
    <>
      <Toolbar className="data-prepare__header" variant="dense">
        <Box flexGrow={1}></Box>
        <Navigation
          onForward={onForward}
          onBack={onBack}
          disableForward={disableForward}
          disableBack={inProgress}
        />
      </Toolbar>
      {isUploadingLabels ? (
        <div className="data-prepare__label-view__uploading-labels-container">
          <Waiting
            diameter={100}
            className="waiter-container"
            loadingText={intl.formatMessage({
              id: 'dataPrep.labelUpload.uploadingLabels',
            })}
          />
        </div>
      ) : null}

      {step === DataPrepareStep.LABEL_UPLOAD ? (
        <LabelUpload
          onManual={() => dispatch(setIsAddMoreDialogOpen(true))}
          onChange={additionalLabels => {
            handleAdditionalLabels(additionalLabels);
          }}
        />
      ) : step === DataPrepareStep.LABEL_REVIEW ? (
        <LabelReview
          labels={annotationData?.categories || []}
          onChange={handleUpdatedLabels}
          onAddMoreClick={() => dispatch(setIsAddMoreDialogOpen(true))}
        />
      ) : null}
      <AddLabelDialog
        isOpen={isAddMoreDialogOpen}
        onClose={() => dispatch(setIsAddMoreDialogOpen(false))}
        onSubmit={handleAdditionalLabels}
      />
    </>
  );
};

interface LabelUploadProps {
  onChange: (newLabels: string[]) => void;
  onManual: () => void;
}

export const LabelUpload = ({onChange, onManual}: LabelUploadProps) => {
  const handleLabelUpload = (files: File[]) => {
    // TEMP: parse file on client side for now.
    // This is just for demo, need to handle on server side for real implementation
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = () => {
        const text = reader.result as string;
        if (text) {
          const newLabels = text.split(/[\r\n]+/g).filter(text => text);
          onChange(newLabels);
        }
      };
      reader.readAsText(file);
    });
  };

  return (
    <div className="label-upload">
      <FormattedMessage tagName="h5" id="dataPrep.labelUpload.step" />

      <div>
        <FormattedMessage tagName="span" id="dataPrep.labelUpload.title" />
      </div>
      <DropArea
        className="label-upload__drop"
        onChange={handleLabelUpload}
        accept=".txt"
        size="xlg"
      />
      <Button color="primary" className="label-upload__manual-btn" onClick={onManual}>
        <FormattedMessage tagName="span" id="dataPrep.labelUpload.manualButton" />
      </Button>
    </div>
  );
};

interface AddLabelDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (labels: string[]) => void;
}

export const AddLabelDialog = ({isOpen, onClose, onSubmit}: AddLabelDialogProps) => {
  const [newLabel, setNewLabel] = useState('');
  const [labels, setLabels] = useState<string[]>([]);

  const handleLabelDelete = (removedLabel: string) => {
    setLabels(labels => labels.filter(label => label !== removedLabel));
  };

  const handleLabelAdd: React.FormEventHandler = event => {
    event.preventDefault();
    if (newLabel) {
      setLabels(mergeLabelCollections(labels, [newLabel]));
      setNewLabel('');
    }
  };

  const handleSubmit = () => {
    onSubmit(labels);
    // reset form
    setLabels([]);
    setNewLabel('');
    onClose();
  };

  return (
    <Dialog open={isOpen} className="add-label-dialog" onClose={onClose}>
      <h2 className="add-label-dialog__title">Add Labels</h2>
      <div className="add-label-dialog__body">
        <form onSubmit={handleLabelAdd}>
          <StudioTextField
            className="add-label-dialog__input"
            autoFocus
            InputProps={{
              placeholder: 'Type a label name',
              onChange: event => {
                setNewLabel(event.target.value);
              },
              value: newLabel,
            }}
          />
          <div className="add-label-dialog__submit">
            <IconButton type="submit" color="primary" disabled={!newLabel}>
              <AddCircleRounded />
            </IconButton>
            <span>or press enter</span>
          </div>
        </form>
        <LabelList labels={labels} onDelete={handleLabelDelete} />
      </div>
      <div className="add-label-dialog__actions">
        <FormButton
          buttonRole="secondary"
          value={'Cancel'}
          onClick={onClose}
          type="button"
        />
        <FormButton
          buttonRole="primary"
          value="Submit"
          type="submit"
          disabled={!labels.length}
          onClick={handleSubmit}
        />
      </div>
    </Dialog>
  );
};

interface LabelListProps {
  className?: string;
  labels: string[];
  onDelete: (label: string) => void;
}

const LabelList = ({labels, onDelete, className}: LabelListProps) => (
  <ul className={cn('label-list', className)}>
    {labels
      .sort((a, b) => a.localeCompare(b))
      .map((label, index) => (
        <li key={index} className="label-list__chip">
          {label}
          <div
            className="label-list__delete"
            role="button"
            onClick={() => onDelete(label)}
          >
            <Close />
          </div>
        </li>
      ))}
  </ul>
);

const mergeLabelCollections = (existingLabels: string[], newLabels: string[]) => {
  const merged: string[] = [...existingLabels];
  newLabels.forEach(newLabel => {
    const isExistingLabel = merged.find(
      label => label.toUpperCase() === newLabel.toUpperCase()
    );
    if (!isExistingLabel) {
      merged.push(newLabel);
    }
  });
  return merged;
};
